// ============================================================================
// Shared cache-invalidation contracts for the media-accreditation module.
//
// Each constant is the single source of truth for "which query caches render
// this entity", so a mutation can never refresh half the views that show the
// row it just changed. Hooks loop the constant:
//
//     KEYS.forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
//
// The strings are the FIRST segment of the real queryKey tuples used by the
// media hooks (TanStack Query invalidates by key prefix, so a bare first
// segment matches every filtered/id-scoped variant of that query).
// Pinned by hooks/mediaInvalidation.test.ts.
// ============================================================================

/**
 * Reviewing (approving/rejecting) a single accreditation request.
 * A request is a child row of a demand, so the parent demand views and the
 * dashboard counters go stale too.
 */
export const REVIEW_REQUEST_INVALIDATES: string[] = [
    "accreditation-requests",
    "accreditation-request",
    "accreditation-demands",
    "accreditation-demand",
    "accreditation-stats",
    // Per-(organization, match) "x / 2" counter of the demand drawer.
    "accreditation-org-match-quota",
];

/**
 * Reviewing a whole accreditation demand. Demand-spawned child rows are normal
 * accreditation_requests, so the single/list request views and the stats
 * counters must refresh alongside the demand views.
 */
export const REVIEW_DEMAND_INVALIDATES: string[] = [
    "accreditation-demands",
    "accreditation-demand",
    "accreditation-requests",
    "accreditation-request",
    "accreditation-stats",
    "accreditation-org-match-quota",
];

/**
 * Verifying / rejecting / suspending a journalist. Request cards embed the
 * personnel row (they render `is_verified`), so the request lists are stale
 * as soon as the personnel row changes.
 */
export const PERSONNEL_MUTATION_INVALIDATES: string[] = [
    "media-personnel",
    "accreditation-requests",
];

/**
 * Org-level agrément / document / login mutations. Whether an org may request
 * accreditations is derived from its agrément + provisioning state, which the
 * per-league access views also read.
 */
export const ORG_ACCESS_INVALIDATES: string[] = [
    "media-organizations",
    "org-league-access",
];

/**
 * Granting / rejecting / revoking an organization's access to a league
 * (review-league-access). The "Accès ligues" queue, the per-league section of
 * the organization drawer and the organization rows all render that row.
 */
export const LEAGUE_ACCESS_REVIEW_INVALIDATES: string[] = [
    "league-access-requests",
    "org-league-access",
    "media-organizations",
];

/**
 * Deleting a journalist or an organisme (soft delete). The account leaves
 * every league-facing list, its upcoming requests / demands / league accesses
 * change status, and the counters that read them move.
 */
export const DELETE_MEDIA_ACCOUNT_INVALIDATES: string[] = [
    "media-organizations",
    "media-personnel",
    "league-access-requests",
    "org-league-access",
    "accreditation-demands",
    "accreditation-demand",
    "accreditation-requests",
    "accreditation-request",
    "accreditation-stats",
    "accreditation-org-match-quota",
    "media-activity",
    "media-org-owner-sign-ins",
    // Scan mode's per-match count of today's approved accreditations: the
    // deletion revokes the upcoming ones.
    "media-scan-mode-approved-counts",
];
